import type { ResumoEsteira } from '../services/jornais';
import estilos from './FaixaDaEsteira.module.css';

/**
 * As 13 etapas da esteira em uma tira, para a redação ler a fila inteira do
 * jornal de relance e clicar no que está vermelho.
 *
 * A faixa desenha SEMPRE as 13, mesmo as que ainda não têm linha no livro:
 * buraco na tira faz o editor achar que a etapa sumiu, quando ela só não
 * chegou a rodar.
 */

/** Ordem e rótulo curto de cada etapa. O rótulo longo fica na tela da esteira. */
const CURTO: Record<string, string> = {
  recepcao: 'lauda',
  midia: 'VT',
  transcricao: 'fala',
  normalizacao: 'norm',
  materia: 'texto',
  manchete: 'título',
  publicacao_site: 'site',
  video_integral_16x9: '16:9',
  video_integral_9x16: '9:16',
  video_highlight_16x9: 'corte',
  video_short_9x16: 'short',
  publicacao_youtube: 'YT',
  publicacao_redes: 'redes',
};

interface Props {
  catalogo: { etapa: string; ordem: number; rotulo: string }[];
  resumo?: ResumoEsteira;
  /** Compacta: só as marcas, sem rótulo. Usada na lista. */
  compacta?: boolean;
  onEtapa?: (etapa: string) => void;
}

export function FaixaDaEsteira({ catalogo, resumo, compacta = false, onEtapa }: Props) {
  const porEtapa = new Map((resumo?.faixa ?? []).map((f) => [f.etapa, f]));

  return (
    <ol className={compacta ? estilos.faixaCompacta : estilos.faixa} aria-label="Etapas da esteira">
      {catalogo.map((c) => {
        const linha = porEtapa.get(c.etapa);
        const estado = linha?.estado ?? 'ausente';
        const manual = linha?.manual ?? false;
        const titulo = `${c.rotulo}: ${manual ? 'corrigida à mão' : estadoEmPalavras(estado)}`;
        return (
          <li key={c.etapa} className={estilos.item}>
            <button
              type="button"
              className={`${estilos.marca} ${estilos[estado] ?? estilos.ausente} ${
                manual ? estilos.manual : ''
              }`}
              title={titulo}
              aria-label={titulo}
              onClick={onEtapa ? () => onEtapa(c.etapa) : undefined}
              disabled={!onEtapa}
            >
              {!compacta && <span className={estilos.rotulo}>{CURTO[c.etapa] ?? c.etapa}</span>}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** O estado em palavras — o `title` é o que um leitor de tela vai ler. */
export function estadoEmPalavras(estado: string): string {
  switch (estado) {
    case 'pronto':
      return 'pronta';
    case 'rodando':
      return 'rodando agora';
    case 'falhou':
      return 'falhou';
    case 'pulada':
      return 'não se aplica a esta lauda';
    case 'manual':
      return 'corrigida à mão';
    case 'pendente':
      return 'à espera';
    default:
      return 'ainda não rodou';
  }
}
